import { ActionReducerMapBuilder, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RecipeType, SaveRecipeRequestInfo, ThingType, RecipeWidgetMeta } from '@kemu-io/kemu-types';
import { WidgetGroupState, WidgetState, WidgetType } from '@kemu-io/kemu-core/types';
import { NavigateFunction } from 'react-router-dom';
import { InterfaceState, showGlobalNotification, showPremiumUpgradeModal } from '../interfaceSlice';
import { setRecipeMetaAction, setRecipeNeedsSaving } from '../../Workspace/workspaceSlice';
import { generateRecipePackage } from '../../../common/recipeActions/saveRecipe';
import routes from '@common/routes/index';
import { SUBSCRIPTION_LIMIT_REACHED, USER_CHAT_PROPERTIES } from '@common/constants';
import getFreshChatWidget from '@src/app/recipe/freshChatWidget';
import recipeApi from '@src/api/recipe/recipeApi';
import { RootState } from '@src/app/store';
import { getGlobalIntl } from '@src/assets/i18n/globalIntl';
import { decodeServerErrorInfo } from '@src/app/reducers/widget/utils';

// 6MB limit
const REQUEST_LIMIT = 1024 * 1024 * 6;

/**
 * Uploads a recipe to the server.
 * If the recipe uses a storage unit, it uses multipart upload.
 * @param options.recipePoolId the id of the recipe in the pool
 * @returns the sha1 of the combined storage (if present)
 */
export const storeRecipeAction = createAsyncThunk('/interface/storeRecipe', async (options: {
	recipePoolId: string,
	newName?: string,
	hideProgress?: boolean
	navigate: NavigateFunction,
	saveAsNew?: boolean
}, thunkAPI): Promise<string | null> => {

	try {
		const t = getGlobalIntl();

		// Show saving notification
		if (!options.hideProgress) {
			thunkAPI.dispatch(showGlobalNotification({
				code: 'save_recipe',
				message: t('Recipe.AsyncIndicator.Progress', { action: t('CommonWords.Saving') }),
				showProgress: true,
				duration: 30,
				type: 'info',
			}));
		}


		const isCreateCopy = Boolean(options.newName || options.saveAsNew);
		const { interface: interfaceState } = thunkAPI.getState() as RootState;
		const { contents, storage, parsedContents: recipeCopy, author, dbInfo } = await generateRecipePackage({
			recipePoolId: options.recipePoolId,
			name: options.newName,
			createNewUuid: isCreateCopy,
			saveGlobalVariableValues: interfaceState.logicMapperSettings.saveGlobalVariableValues,
		});

		const blocksKeys = Object.keys(recipeCopy.blocks);
		const storageSize = storage?.size || 0;
		const checksum = storage?.checksum || '0';

		const widgetsMeta: RecipeWidgetMeta[] = [];
		blocksKeys.forEach(blockId => {
			const widgets = recipeCopy.blocks[blockId].gates;
			Object.keys(widgets).forEach(widgetId => {
				const widget = recipeCopy.blocks[blockId].gates[widgetId];
				widgetsMeta.push({
					// TODO: Replace once users can rename widgets
					name: widget.type === WidgetType.widgetGroup ? (widget.state as WidgetState<WidgetGroupState>).name : widget.type,
					thingId: blockId,
					type: widget.type,
				});
			});
		});

		const parsedRecipe: SaveRecipeRequestInfo = {
			// id: recipeCopy.id,
			name: recipeCopy.name,
			dataSources: recipeCopy.dataSources,
			protocolVersion: recipeCopy.protocolVersion,
			uuid: recipeCopy.uuid,
			blocks: blocksKeys.map((blockId) => {
				// Some older recipes (before cloud recipes were introduced - and later decommissioned -)
				// had a `thing.type` that was equal to the thing's id and did not match the current type (cloud, web).
				// Here we make sure that if an older recipe is opened and saved, the thing type is updated to match
				// a valid type (default web);
				const thisThing = recipeCopy.blocks[blockId];
				if (thisThing.type !== ThingType.Cloud && thisThing.type !== ThingType.Web) {
					thisThing.type = ThingType.Web;
				}

				return {
					name: thisThing.name,
					category: thisThing.category,
					id: thisThing.id,
					type: thisThing.type,
					version: thisThing.version,
				};
			}),

			widgets: widgetsMeta,
		};


		// Append cloud environment info to the recipe (if any)
		const { workspace } = thunkAPI.getState() as RootState;
		if (workspace.currentRecipe.cloud) {
			parsedRecipe.cloud = { ...workspace.currentRecipe.cloud };
		}

		// Make sure the contents don't surpass the request limit
		const encoder = new TextEncoder();
		const contentsBuffer = encoder.encode(contents);
		if (contentsBuffer.byteLength > REQUEST_LIMIT) { throw new Error(`Recipe contents exceed the allowed limit`); }


		// const contentAb = contentsBuffer.buffer.slice(contentsBuffer.byteOffset, contentsBuffer.byteOffset + contentsBuffer.byteLength) as ArrayBuffer;
		const recipeName = options.newName || parsedRecipe.name;
		// const canUploadStorage = (uploadUrl?: string) => !!(uploadUrl && storage?.disk !== undefined && storage.disk.byteLength);

		// const handleDiskUpload = (uploadUrl?: string) => {
		// 	// If we need to upload the contents, dispatch a new event
		// 	if (storage?.disk !== undefined && storage.disk.byteLength && uploadUrl) {
		// 		return thunkAPI.dispatch(uploadRecipeStorageAction({ name: recipeName, disk: storage.disk, uploadUrl }));
		// 	}
		// };

		// Will store the final id of the recipe
		let recipeID: string;

		if (!dbInfo?.id || isCreateCopy) {

			const response = await recipeApi.saveRecipe({
				rawContents: contents,
				recipeData: parsedRecipe,
				contentLength: contentsBuffer.byteLength,
				storageSize,
				storageChecksum: checksum,
			});

			// const uploadStorage = canUploadStorage(response.storageUrl);

			// Update the ID of the local recipe with that provided by the server
			// setRecipeMeta(options.recipeId, {
			// 	id: response.recipeId,
			// 	version: response.version,
			// 	name: recipeName,
			// });

			thunkAPI.dispatch(setRecipeMetaAction({
				dbId: response.recipeId,
				version: response.version,
				name: recipeName,
				moreVersionsAvailable: false,
				uuid: recipeCopy.uuid,
				recipeType: workspace.currentRecipe.cloud ? RecipeType.Cloud : RecipeType.Browser,
				versionHistory: response.versionHistory || [],
				loadedFromDisk: false,
			}));

			// Also update the title of the recipe
			// thunkAPI.dispatch(setRecipeName(recipeName));
			// Upload contents
			// await thunkAPI.dispatch(uploadRecipeContentsAction({
			// 	contents: contentAb,
			// 	uploadUrl: response.contentsUrl,
			// 	hasStorage: uploadStorage,
			// }));

			// Upload storage
			// if (uploadStorage && storage?.disk && response.storageUrl) {
			// 	// await thunkAPI.dispatch(uploadRecipeStorageAction({ name: recipeName, disk, uploadUrl: response.storageUrl }));
			// 	await handleDiskUpload(response.storageUrl);
			// }

			recipeID = response.recipeId;
		} else {
			const response = await recipeApi.updateRecipe({
				recipeInfo: parsedRecipe,
				rawContents: contents,
				recipeId: dbInfo?.id,
				contentLength: contentsBuffer.byteLength,
				storageSize,
				storageChecksum: checksum,
				currentVersion: workspace.currentRecipe.currentVersion,
			});

			thunkAPI.dispatch(setRecipeMetaAction({
				dbId: response.recipeId,
				version: response.version,
				name: recipeName,
				uuid: recipeCopy.uuid,
				moreVersionsAvailable: !!response.moreVersionsAvailable,
				recipeType: workspace.currentRecipe.type,
				versionHistory: response.versionHistory || []
			}));
			
			// The server determines based on the checksum if contents need to be uploaded
			// const uploadDiskAgain = canUploadStorage(response.storageUrl);

			// Upload contents
			// await thunkAPI.dispatch(uploadRecipeContentsAction({
			// 	contents: contentAb,
			// 	uploadUrl:
			// 	response.contentsUrl,
			// 	hasStorage: uploadDiskAgain,
			// }));

			// if (!uploadDiskAgain && !options.hideProgress) {
			// 	thunkAPI.dispatch(setAsyncOperationDetails({ extra: false }));
			// }

			// Upload storage
			// TODO: The first time a recipe is opened, the disk ids are re-generated,
			// this causes the storage checksum to change and forces the user to re-upload
			// the disk even though it hasn't really changed.
			// await handleDiskUpload(response.storageUrl);
			recipeID = response.recipeId;
		}

		// TODO: Refactor? having this here is ugly!
		// Update the user properties in the chat widget. This allows support
		// personnel to know which recipe the user is looking at.
		// const freshChatInstance = getFreshChatWidget();
		// if (freshChatInstance.initialized && recipeID) {
		// 	freshChatInstance.setUserProperty({
		// 		[USER_CHAT_PROPERTIES.LastOpenedRecipeId]: recipeID,
		// 		[USER_CHAT_PROPERTIES.LastOpenedRecipeName]: recipeName,
		// 	});
		// }

		// Show the created id in the url
		options.navigate(routes.recipe.getBrowserRecipeRoute(recipeID));

		// Indicates the recipe no longer needs to be saved
		await thunkAPI.dispatch(setRecipeNeedsSaving(false));

		// Show success notification
		if (!options.hideProgress) {
			thunkAPI.dispatch(showGlobalNotification({
				code: 'save_recipe',
				message: t('Recipe.AsyncIndicator.Success', { action: t('CommonWords.Saved') }),
				type: 'success',
			}));
		}

		return checksum;
	} catch (error) {
		const { errorCode, message } = decodeServerErrorInfo(error);
		const t = getGlobalIntl();
		console.error('Failed to save recipe:', error);
		
		// Show error notification
		thunkAPI.dispatch(showGlobalNotification({
			code: 'save_recipe',
			title: t('Recipe.AsyncIndicator.Failure.Title', { action: t('CommonWords.Save') }),
			message: message,
			type: 'error',
		}));

		if(errorCode === SUBSCRIPTION_LIMIT_REACHED){
			thunkAPI.dispatch(showPremiumUpgradeModal(true));
		}

		return null;
	}
});


export const storeRecipeReducer = ((builder: ActionReducerMapBuilder<InterfaceState>): void => {
	builder.addCase(storeRecipeAction.fulfilled, (state, action: PayloadAction<string | null>) => {
		state.lastStorageChecksum = action.payload;
	});
});
